/**
 * DRPX Courier — Auth-Wrapper (Supabase)
 *
 * Wrapper um `_sb.auth.*` mit konsistenten Return-Shapes. Im Gegensatz zu
 * den anderen Supabase-Modulen liefern diese Funktionen nicht das
 * `{ data, error }` Schema von `api.ts` — Auth ist ein eigener Bereich
 * mit eigenem `AuthResult<T>` Discriminated-Union-Typ und einem
 * strukturierten `AuthError` (mit Supabase-Code und HTTP-Status).
 *
 * Caller rufen diese Funktionen direkt auf, nicht über `api()`.
 *
 * @module lib/supabase/auth
 */

import { _sb } from './client.js';
import type {
  Session,
  User,
  Subscription,
  AuthChangeEvent,
  AuthError as SupabaseAuthError,
} from '@supabase/supabase-js';

// ════════════════════════════════════════════════════════════════════
// Public types
// ════════════════════════════════════════════════════════════════════

/**
 * Strukturierter Auth-Fehler.
 *
 * `code` ist der Supabase-Auth-Error-Code (z.B. `'invalid_credentials'`,
 * `'email_not_confirmed'`, `'user_already_exists'`), wenn vorhanden.
 * Caller können darauf via Code reagieren statt über String-Matching der
 * Message.
 */
export interface AuthError {
  message: string;
  code?: string;
  status?: number;
}

/**
 * Discriminated Union für Auth-Operationen.
 *
 * Genau eines von `data` / `error` ist non-null. TypeScript narrowed
 * automatisch nach einem `if (res.error)` Check.
 *
 * Beispiel:
 * ```ts
 * const res = await db.signIn(email, pw);
 * if (res.error) {
 *   console.error(res.error.message);
 *   return;
 * }
 * // res.data ist hier garantiert non-null
 * console.log(res.data.user.id);
 * ```
 */
export type AuthResult<T> =
  | { data: T; error: null }
  | { data: null; error: AuthError };

/**
 * Input-Metadaten für `signUp`. Wird in `auth.users.raw_user_meta_data`
 * gespeichert und von der App nach dem Insert in das Profile-Objekt
 * übertragen.
 */
export interface SignUpMetadata {
  rolle?: string;
  firma?: string;
  [key: string]: unknown;
}

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

/**
 * Einheitlicher Fehler, wenn der Supabase-Client nicht initialisiert ist
 * (z.B. wenn die .env-Variablen fehlen).
 */
const NO_CLIENT_ERROR: AuthError = {
  message: 'Supabase client not initialized',
  code: 'no_client',
};

/**
 * Konvertiert einen Supabase `AuthError` in unseren strukturierten
 * `AuthError`. Übernimmt `message`, `code` und `status` falls vorhanden.
 */
function toAuthError(err: SupabaseAuthError): AuthError {
  const out: AuthError = { message: err.message };
  if (err.code) out.code = err.code;
  if (typeof err.status === 'number') out.status = err.status;
  return out;
}

/**
 * Konvertiert ein `unknown` (typisch aus `catch (e: unknown)`) in einen
 * `AuthError`. Erkennt sowohl `Error`-Instanzen als auch String-Errors.
 */
function unknownToAuthError(err: unknown): AuthError {
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

/**
 * Helper: erfolgreiches Result mit Daten.
 */
function ok<T>(data: T): { data: T; error: null } {
  return { data, error: null };
}

/**
 * Helper: Fehler-Result.
 */
function fail(error: AuthError): { data: null; error: AuthError } {
  return { data: null, error };
}

// ════════════════════════════════════════════════════════════════════
// AUTH OPERATIONS
// ════════════════════════════════════════════════════════════════════

/**
 * Registriert einen neuen User via Supabase Auth.
 *
 * Bei Erfolg ist `data.user` meist gesetzt — kann aber `null` sein, wenn
 * Email-Confirmation aktiviert ist und der User noch nicht bestätigt hat.
 */
export async function signUp(
  email: string,
  password: string,
  metadata?: SignUpMetadata,
): Promise<AuthResult<{ user: User | null }>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.signUp({
      email: email,
      password: password,
      options: {
        data: metadata,
        // Bestätigungslink führt dahin zurück, wo registriert wurde
        // (Dev → localhost, Live → pages.dev). Muss in Supabase unter
        // Auth → URL Configuration → Redirect URLs erlaubt sein, sonst
        // Fallback auf Site URL.
        emailRedirectTo: window.location.origin,
      },
    });
    if (r.error) return fail(toAuthError(r.error));
    return ok({ user: r.data.user });
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Meldet einen User an via E-Mail + Passwort.
 *
 * Bei Erfolg sind sowohl `data.user` als auch `data.session` non-null —
 * Supabase garantiert beides bei `signInWithPassword` Erfolg.
 */
export async function signIn(
  email: string,
  password: string,
): Promise<AuthResult<{ user: User; session: Session }>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.signInWithPassword({
      email: email,
      password: password,
    });
    if (r.error) return fail(toAuthError(r.error));
    // Defensive Runtime-Checks, obwohl Supabase beides garantiert —
    // schützt gegen unerwartete API-Änderungen und macht das Type-Narrowing
    // an den Caller-Stellen unnötig.
    if (!r.data.user || !r.data.session) {
      return fail({
        message: 'Sign-in succeeded but user or session is missing',
        code: 'missing_session',
      });
    }
    return ok({ user: r.data.user, session: r.data.session });
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Meldet den aktuellen User ab.
 *
 * Im Gegensatz zum alten JS-Code propagiert diese Variante Fehler
 * (Netzwerk weg, Token kaputt) statt sie still zu schlucken. Caller
 * können entscheiden ob sie den Fehler ignorieren wollen.
 */
export async function signOut(): Promise<AuthResult<null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.signOut();
    if (r.error) return fail(toAuthError(r.error));
    return ok(null);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Liefert die aktuelle Auth-Session (enthält User-Objekt und JWT-Token).
 * Wird beim App-Start aufgerufen, um den Login-Status zu prüfen.
 *
 * Bei `data: null` und `error: null` ist der User schlicht nicht
 * eingeloggt — das ist kein Fehlerfall.
 */
export async function getSession(): Promise<AuthResult<Session | null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.getSession();
    if (r.error) return fail(toAuthError(r.error));
    return ok(r.data.session);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Schickt eine Passwort-Reset-E-Mail an die angegebene Adresse.
 * Der Link in der Mail führt zurück auf die aktuelle Seite und triggert
 * dort den Password-Update-Flow.
 */
export async function resetPasswordForEmail(
  email: string,
): Promise<AuthResult<null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + window.location.pathname,
    });
    if (r.error) return fail(toAuthError(r.error));
    return ok(null);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Löst den Nachweis aus dem Passwort-Link ein (Seite `/recovery`).
 *
 * Hintergrund (12.10.2026): Mit `flowType: 'pkce'` funktionierte der
 * Standard-Link aus der Mail nur im selben Browser, der „Passwort
 * vergessen" angeklickt hatte (der geheime Schlüssel liegt dort), und das
 * Signal `PASSWORD_RECOVERY` kam nie. Die Mailvorlage „Reset Password"
 * verlinkt deshalb auf `/recovery?token_hash=…&type=recovery`; dieser
 * Nachweis braucht keinen Schlüssel im Browser und klappt überall — auch
 * beim Zurücksetzen durch den Admin.
 *
 * Bei Erfolg legt Supabase die Sitzung an und meldet `PASSWORD_RECOVERY`.
 * Ein Link gilt nur einmal und begrenzte Zeit. Seit 17.10.2026 ruft das
 * Formular „Neues Passwort“ diese Funktion erst beim SPEICHERN auf, nicht
 * mehr beim Laden der Seite — Virenprüfer von Mail-Diensten, die Links vorab
 * öffnen, verbrauchten ihn sonst (siehe `useAuth.recoveryToken`).
 *
 * @param tokenHash Wert von `token_hash` aus der Adresse
 * @returns `ok(null)` bei Erfolg, sonst Fehler (z. B. `otp_expired`)
 */
export async function verifyRecoveryToken(
  tokenHash: string,
): Promise<AuthResult<null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    // Erst den Start des Clients abwarten (liefert das laufende Versprechen,
    // startet nichts neu) — verifyOtp selbst wartet nicht darauf, und eine
    // gleichzeitige Sitzungs-Wiederherstellung könnte die neue Sitzung
    // sonst überschreiben.
    await client.auth.initialize();
    const r = await client.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
    if (r.error) return fail(toAuthError(r.error));
    return ok(null);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Löst den Link aus der Team-Einladung ein (DRPX-Pro, Migration 141):
 * `/einladung?token_hash=…&type=invite` aus der Supabase-Vorlage
 * „Invite user". Wie beim Passwort-Link erst beim Speichern im Formular,
 * nicht beim Laden der Seite (Virenprüfer öffnen Links vorab).
 *
 * Bei Erfolg legt Supabase die Sitzung an und meldet `SIGNED_IN`;
 * `useAuth` übergeht das, solange das Einladungs-Formular offen ist.
 *
 * @param tokenHash Wert von `token_hash` aus der Adresse
 * @returns `ok(null)` bei Erfolg, sonst Fehler (z. B. `otp_expired`)
 */
export async function verifyEinladungToken(
  tokenHash: string,
): Promise<AuthResult<null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    await client.auth.initialize();
    const r = await client.auth.verifyOtp({ token_hash: tokenHash, type: 'invite' });
    if (r.error) return fail(toAuthError(r.error));
    return ok(null);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Aktualisiert das Passwort des aktuell eingeloggten Users.
 * Wird nach einem Reset-Flow oder im Profil-Settings-Dialog aufgerufen.
 */
export async function updatePassword(
  newPassword: string,
): Promise<AuthResult<null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.updateUser({ password: newPassword });
    if (r.error) return fail(toAuthError(r.error));
    return ok(null);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}

/**
 * Registriert einen Callback für Auth-State-Änderungen (Login, Logout,
 * Token-Refresh). Dies ist die zentrale Wahrheit für den Login-Status
 * in der App — wird in `useAuth.js` als Single Source of Truth verwendet.
 *
 * Synchrone Funktion (kein `AuthResult`-Wrapper). Gibt `null` zurück,
 * wenn der Supabase-Client nicht initialisiert ist — Caller muss das
 * mit `if (sub)` prüfen, bevor `sub.unsubscribe()` aufgerufen wird.
 *
 * @param cb Callback `(event, session) => void` für Auth-Events.
 * @returns Subscription-Objekt (mit `.unsubscribe()`) oder `null`.
 */
export function onAuthStateChange(
  cb: (event: AuthChangeEvent, session: Session | null) => void | Promise<void>,
): Subscription | null {
  if (!_sb) return null;
  const r = _sb.auth.onAuthStateChange(cb);
  return r.data.subscription;
}

/**
 * Sendet die Bestätigungs-Mail für einen Signup erneut.
 *
 * Wird genutzt, wenn ein User nach dem Signup die Bestätigungs-Mail nicht
 * erhalten hat (z.B. im Spam-Ordner gelandet). Supabase verschickt eine
 * neue Mail an dieselbe Adresse.
 *
 * Rate-Limit:
 *   Supabase-Auth setzt ein serverseitiges Rate-Limit für diese Funktion
 *   (typisch ca. 1 Mail pro Minute pro Email). Frontend sollte zusätzlich
 *   einen Cooldown-Timer nach dem Klick anzeigen.
 *
 * @param email - Die Email des Users (muss mit der ursprünglichen
 *                Signup-Email übereinstimmen).
 * @returns `{ data: null }` bei Erfolg, `{ error: AuthError }` bei Fehler.
 */
export async function resendConfirmation(
  email: string,
): Promise<AuthResult<null>> {
  if (!_sb) return fail(NO_CLIENT_ERROR);
  const client = _sb;
  try {
    const r = await client.auth.resend({
      type: 'signup',
      email: email,
    });
    if (r.error) return fail(toAuthError(r.error));
    return ok(null);
  } catch (e: unknown) {
    return fail(unknownToAuthError(e));
  }
}
